<script setup lang="ts">
/**
 * The first ten minutes, guided by a knight.
 *
 * Shaped after how the genre does this, and after what actually goes wrong
 * without it: a new lord lands on a village screen, has no idea that the plot
 * is clickable, builds nothing, and leaves. So the rules here are:
 *
 *  - **It follows the real tasks.** Every step is something the task chain was
 *    going to ask for anyway, so the tutorial is not a detour from the game,
 *    it is the game with a hand on your shoulder. Nothing here grants
 *    anything or fakes anything.
 *  - **It advances on the deed, not on a button.** Where a step has a
 *    watchable outcome, doing the thing moves it on; only the steps that are
 *    pure explanation carry a Next.
 *  - **It is skippable at every step**, and once skipped or finished it never
 *    returns. That choice lives in localStorage, per account.
 *  - **It never blocks the screen.** No modal, no dimming, no captured
 *    clicks: a spotlight ring points at what matters and the player is free
 *    to ignore it entirely.
 *
 * Deliberately short. It teaches building, the queue, collecting a task, the
 * map, and recruiting, then gets out of the way. Marches, alliances, the
 * market and the rest are left for the tasks and the manual.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { PAGE_ART } from '@/assets/art/art';
import { play } from '@/audio';
import { useAuthStore } from '@/stores/auth';
import { useVillageStore } from '@/stores/village';

const villages = useVillageStore();
const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

const STORAGE_KEY = 'waldermarch.tutorial.v1';

interface Step {
  /** What the knight says. Two short sentences at most. */
  say: string;
  /** CSS selector to ring, if any. */
  spotlight?: string;
  /** True once the player has done it; when set, no Next button is shown. */
  done?: () => boolean;
  /** Where the step wants the player to be. */
  route?: 'village' | 'map';
}

const totalLevels = computed(() =>
  villages.buildings.reduce((sum, b) => sum + b.level, 0),
);
const timberLevel = computed(
  () => villages.buildings.find((b) => b.type === 'timber_camp')?.level ?? 0,
);
const queueLength = computed(() => villages.buildQueue.length);
const troops = computed(() => {
  const home = villages.army?.home ?? {};
  return Object.values(home).reduce((sum, n) => sum + (n ?? 0), 0);
});
const training = computed(() => (villages.army?.trainingQueue ?? []).length);

const STEPS: Step[] = [
  {
    say: 'My lord. This is your holding, and everything in it is yours to raise. I will walk you through the first of it.',
    route: 'village',
  },
  {
    say: 'Your stores sit up here: timber, clay, iron and grain. Everything you build is paid for out of them, and they fill on their own while you are away.',
    spotlight: '.resources',
    route: 'village',
  },
  {
    say: 'Timber first, always. Click the Timber Camp out at the treeline, then raise it.',
    // The one building, not the whole plot. Ringing the entire village to say
    // "click the Timber Camp" pointed at everything and therefore at nothing,
    // which is the exact failure a spotlight exists to prevent.
    spotlight: '.plot__slot[data-building="timber_camp"]',
    route: 'village',
    done: () => timberLevel.value >= 2 || queueLength.value > 0,
  },
  {
    say: 'Good. The work takes time, and the Construction list keeps count. When a build has under three minutes left you may finish it at no cost.',
    spotlight: '.village__side',
    route: 'village',
    done: () => queueLength.value === 0 && totalLevels.value > 4,
  },
  {
    say: 'Your tasks are listed here, and each pays for itself. Collect what you have earned.',
    spotlight: '.tasks',
    route: 'village',
  },
  {
    say: 'Now the world. Out there are your neighbours, and the bandit camps worth raiding for coin.',
    spotlight: '.topbar__nav',
    route: 'village',
    done: () => route.name === 'map',
  },
  {
    say: 'Drag to look around, scroll to close in. Click any holding and a ring of orders opens around it.',
    spotlight: '.mapcanvas',
    route: 'map',
  },
  {
    say: 'Last thing: raise some men. Open the Barracks in your village and put a few spearmen to the drill.',
    // In the village, on the Barracks. It used to ring the top navigation from
    // the map screen, which is neither the place nor the thing.
    spotlight: '.plot__slot[data-building="barracks"]',
    route: 'village',
    done: () => troops.value > 0 || training.value > 0,
  },
  {
    say: 'That is the whole of it, my lord. Follow your tasks and they will not steer you wrong. I will be at the gate.',
    route: 'village',
  },
];

const index = ref(0);
const active = ref(false);
const step = computed(() => STEPS[index.value]);
const isLast = computed(() => index.value === STEPS.length - 1);
const needsButton = computed(() => !step.value?.done);

function storageKey(): string {
  return `${STORAGE_KEY}.${auth.user?.id ?? 'anon'}`;
}

function finish(): void {
  active.value = false;
  clearSpotlight();
  try {
    localStorage.setItem(storageKey(), 'done');
  } catch {
    // Private browsing: the tutorial simply offers itself again.
  }
}

function next(): void {
  play('click');
  if (isLast.value) {
    finish();
    return;
  }
  index.value += 1;
}

/** Ring whatever the current step points at, without capturing any clicks. */
let ringed: Element | null = null;

function clearSpotlight(): void {
  ringed?.classList.remove('tutorial-target');
  ringed = null;
}

function applySpotlight(): void {
  clearSpotlight();
  const selector = step.value?.spotlight;
  if (!active.value || !selector) return;

  /*
   * Wait for the thing to exist.
   *
   * A step can name something that has not rendered yet: the route may still
   * be changing, and the plot rebuilds its slots whenever the village data
   * arrives. A single querySelector at step time silently found nothing and
   * the player got no ring at all, which reads as the tutorial being broken
   * rather than as a race. Retried for a couple of seconds, then given up on.
   */
  const tryFind = (attempt: number): void => {
    if (!active.value || step.value?.spotlight !== selector) return;
    const el = document.querySelector(selector);
    if (el) {
      el.classList.add('tutorial-target');
      ringed = el;
      // Scroll it into view: a ring below the fold helps nobody.
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      return;
    }
    if (attempt < 20) window.setTimeout(() => tryFind(attempt + 1), 100);
  };
  tryFind(0);
}

// The step may point somewhere the player is not; take them there.
watch(
  () => step.value?.route,
  (want) => {
    if (!active.value || !want) return;
    if (route.name !== want) void router.push({ name: want });
  },
);

watch([index, active, () => route.name], () => {
  // Let the target screen paint before hunting for the element.
  window.setTimeout(applySpotlight, 120);
});

// Advance the moment the deed is done, rather than waiting for a click.
watch(
  [timberLevel, queueLength, totalLevels, troops, training, () => route.name],
  () => {
    if (!active.value) return;
    const check = step.value?.done;
    if (check && check()) {
      if (isLast.value) finish();
      else index.value += 1;
    }
  },
);

onMounted(() => {
  let seen = 'done';
  try {
    seen = localStorage.getItem(storageKey()) ?? '';
  } catch {
    seen = 'done';
  }
  // Only for a genuinely new lord: one village, barely built.
  const fresh = villages.villages.length <= 1 && totalLevels.value <= 8;
  if (!seen && fresh) {
    active.value = true;
    window.setTimeout(applySpotlight, 400);
  }
});

/*
 * Tell the rest of the page the knight is standing in the bottom-left corner,
 * so anything else that wants it can step aside. The toasts do.
 */
watch(active, (on) => {
  document.body.classList.toggle('has-tutor', on);
}, { immediate: true });

onBeforeUnmount(() => {
  clearSpotlight();
  document.body.classList.remove('has-tutor');
});
</script>

<template>
  <div v-if="active && step" class="tutor">
    <img class="tutor__knight" :src="PAGE_ART.tutor" alt="" aria-hidden="true" />

    <div class="tutor__scroll">
      <div class="tutor__head">
        <span class="tutor__who">Ser Aldric</span>
        <span class="tutor__count">{{ index + 1 }} of {{ STEPS.length }}</span>
      </div>

      <p class="tutor__say">{{ step.say }}</p>

      <div class="tutor__row">
        <button class="btn btn--sm tutor__skip" @click="finish">Skip</button>
        <span v-if="!needsButton" class="tutor__wait">Waiting on you…</span>
        <button v-else class="btn btn--sm btn--primary" @click="next">
          {{ isLast ? 'Understood' : 'Go on' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tutor {
  position: fixed;
  left: 1rem;
  bottom: 1rem;
  z-index: 180;
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
  max-width: min(430px, calc(100vw - 2rem));
  pointer-events: none;

  /* Only the panel is interactive; the knight is a picture. */
  > * { pointer-events: auto; }
}

.tutor__knight {
  width: 96px;
  flex: none;
  filter: drop-shadow(0 4px 12px rgb(0 0 0 / 60%));
  pointer-events: none;
}

.tutor__scroll {
  flex: 1;
  min-width: 0;
  padding: 0.6rem 0.75rem;
  background: rgb(24 21 17 / 97%);
  border: 1px solid var(--border-strong);
  border-left: 3px solid var(--gold);
  border-radius: var(--radius);
  box-shadow: 0 6px 22px rgb(0 0 0 / 55%);
}

.tutor__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.6rem;
  margin-bottom: 0.25rem;
}

.tutor__who {
  font-family: var(--font-display);
  font-size: 0.95rem;
  color: var(--gold);
}

.tutor__count {
  font-family: var(--font-mono);
  font-size: 0.68rem;
  color: var(--parchment-faint);
}

.tutor__say {
  margin: 0 0 0.5rem;
  font-size: 0.86rem;
  line-height: 1.5;
  color: var(--parchment);
}

.tutor__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tutor__skip { margin-right: auto; }

.tutor__wait {
  font-size: 0.74rem;
  color: var(--parchment-faint);
  font-style: italic;
}

@media (max-width: 700px) {
  .tutor__knight { display: none; }
}
</style>
